/**
 * Composable for sending postMessage events to the parent window
 * Use this in your Nuxt app to communicate with the widget loader
 */
export const useParentMessage = () => {
  const route = useRoute()

  const isShopifyEmbedMode = computed(
    () => String(route.query.embedMode || '') === 'shopify',
  )

  function parseOrigin(value: unknown) {
    if (typeof value !== 'string') return null
    try {
      const origin = new URL(value).origin
      return /^https?:\/\//.test(origin) ? origin : null
    } catch {
      return null
    }
  }

  function allowsWildcardParentOrigin() {
    const embedMode = String(route.query.embedMode || '')
    return !isShopifyEmbedMode.value
      && (embedMode === 'local' || embedMode === 'dev' || import.meta.dev)
  }

  const allowedHostOrigin = computed(() => {
    const origin = parseOrigin(route.query.hostOrigin)
    if (origin) return origin
    return allowsWildcardParentOrigin() ? '*' : null
  })

  const isTrustedOrigin = (origin: string) => {
    if (allowedHostOrigin.value === '*') return true
    return Boolean(allowedHostOrigin.value) && origin === allowedHostOrigin.value
  }

  /**
   * Send a message to the parent window
   */
  const sendToParent = (type: string, data?: unknown) => {
    if (window.parent && window.parent !== window) {
      if (!allowedHostOrigin.value) {
        console.warn('Missing or invalid parent origin for embedded builder')
        return
      }
      window.parent.postMessage({ type, data }, allowedHostOrigin.value)
    } else {
      console.warn('No parent window found (not in iframe)')
    }
  }

  /**
   * Notify parent that the card is complete
   */
  const notifyCardComplete = (cardData?: unknown) => {
    sendToParent('gm/cardComplete', cardData)
  }

  /**
   * Notify parent to close the modal
   */
  const notifyClose = () => {
    sendToParent('gm/cancel')
  }

  /**
   * Check if running in an iframe
   */
  const isInIframe = () => {
    return window.parent !== window
  }

  return {
    sendToParent,
    isTrustedOrigin,
    notifyCardComplete,
    notifyClose,
    isInIframe,
    allowedHostOrigin,
    isShopifyEmbedMode,
  }
}
